// Portal: Users. Self-serve login management for the customer's own
// account -- add a teammate, remove one. Server forces every created
// login into the customer role + this account's scope; the form here
// is just name/email/password.

import { useEffect, useState } from 'react';
import axios from 'axios';

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  if (!y || !m || !d) return '—';
  return `${MONTHS_SHORT[Number(m) - 1] || m} ${d}, ${y}`;
}

export default function PortalUsers() {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  function load() {
    axios.get('/api/portal/users')
      .then((res) => setUsers(res.data))
      .catch(() => setError('Failed to load users. Please try again.'));
  }
  useEffect(load, []);

  async function handleAdd(e) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await axios.post('/api/portal/users', { name, email, password });
      setName(''); setEmail(''); setPassword('');
      load();
    } catch (err) {
      setFormError(err.response?.data?.error || 'Failed to add user.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRemove(u) {
    if (!window.confirm(`Remove ${u.name}'s login? They will no longer be able to sign in.`)) return;
    try {
      await axios.delete(`/api/portal/users/${u.id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to remove user.');
    }
  }

  return (
    <section className="page">
      <div className="page-header">
        <h2>Portal Users</h2>
        {users && <span className="portal-count">{users.length} login{users.length === 1 ? '' : 's'}</span>}
      </div>

      {error && <p className="portal-error">{error}</p>}
      {!users && !error && <p className="placeholder-note">Loading…</p>}

      {users && (
        <div className="portal-users-layout">
          <div className="table-wrap" style={{ flex: 1, minWidth: 340 }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Added</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}{u.is_you && <span className="portal-you"> (you)</span>}</td>
                    <td className="mono">{u.email}</td>
                    <td className="mono">{fmtDate(u.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      {!u.is_you && (
                        <button type="button" className="btn-secondary" onClick={() => handleRemove(u)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form className="portal-form" onSubmit={handleAdd} style={{ maxWidth: 380 }}>
            <h4 style={{ margin: 0, fontSize: 'var(--text-large)' }}>Add a user</h4>
            <p className="portal-footnote" style={{ margin: 0 }}>
              New users see everything in this portal for your sites. Share the
              password with them directly — we recommend they change it by
              asking us for a reset if it's ever shared too widely.
            </p>
            <label>
              Name
              <input className="form-input" value={name} maxLength={100}
                     onChange={(e) => setName(e.target.value)} required />
            </label>
            <label>
              Email
              <input className="form-input" type="email" value={email} maxLength={100}
                     onChange={(e) => setEmail(e.target.value)} required />
            </label>
            <label>
              Password (10+ characters)
              <input className="form-input" type="text" value={password} maxLength={72} minLength={10}
                     onChange={(e) => setPassword(e.target.value)} required
                     autoComplete="new-password" />
            </label>
            {formError && <p className="portal-error">{formError}</p>}
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add user'}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
